import { Card, CardTitle, Subtitle, VBadge } from '@vrzl/ui';
import { Bot } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { useDocuments } from '@/domains/documents';
import { EventHeader, useEvent } from '@/domains/events';
import { ApiError } from '@/domains/general/api/client';

import { buildRequestMessages } from '../api/buildRequestMessages';
import { buildChatPayload } from '../api/chatPayload';
import { useSendChat } from '../api/mutations';
import type { ChatEntry } from '../api/types';

import { ChatComposer } from './chatComposer';
import { ChatDocumentTags } from './chatDocumentTags';
import { ChatMessageList } from './chatMessageList';

const GREETING_CONTENT =
  'Hallo! Ik ben de AI-assistent voor dit evenement. Ik heb alle documenten geanalyseerd. Wat wilt u weten?';

const ERROR_CONTENT =
  'Er is een fout opgetreden bij het verwerken van uw vraag. Probeer het opnieuw.';

const CONNECTION_ERROR_CONTENT =
  'De verbinding met de server werd geblokkeerd of verbroken. Probeer het opnieuw.';

const AI_ERROR_CONTENT = 'De AI-assistent reageerde niet op tijd. Probeer het opnieuw.';

function nowTime(): string {
  return new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

export function Assistant() {
  const { eventId } = useParams();
  const caseId = eventId ?? '';

  const { data: event } = useEvent(caseId);
  const { data: documentsData } = useDocuments(caseId);
  const documents = documentsData?.items ?? [];

  const [entries, setEntries] = useState<ChatEntry[]>(() => [
    {
      id: 'greeting',
      role: 'assistant',
      content: GREETING_CONTENT,
      sources: [],
      timestamp: nowTime(),
      displayOnly: true,
    },
  ]);

  const [typingId, setTypingId] = useState<string | null>(null);

  const sendChat = useSendChat();

  function appendAssistantError(content: string) {
    setEntries((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'assistant',
        content,
        sources: [],
        timestamp: nowTime(),
        displayOnly: true,
      },
    ]);
  }

  function handleSend(text: string) {
    const userEntry: ChatEntry = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      sources: [],
      timestamp: nowTime(),
    };

    const nextEntries = [...entries, userEntry];
    setEntries(nextEntries);

    const messages = buildRequestMessages(nextEntries);

    sendChat.mutate(buildChatPayload(caseId, messages), {
      onSuccess: (response) => {
        const isMalformed = typeof response?.message?.content !== 'string';
        const content = isMalformed ? ERROR_CONTENT : response.message.content;
        const sources = Array.isArray(response?.sources) ? response.sources : [];
        const id = crypto.randomUUID();

        setEntries((prev) => [
          ...prev,
          {
            id,
            role: 'assistant',
            content,
            sources,
            timestamp: nowTime(),
            ...(isMalformed ? { displayOnly: true } : {}),
          },
        ]);
        setTypingId(id);
      },
      onError: (error) => {
        if (error instanceof ApiError) {
          appendAssistantError(
            error.status === 502 || error.status === 504 ? AI_ERROR_CONTENT : ERROR_CONTENT,
          );
          return;
        }

        // Geen ApiError betekent dat `fetch` zelf faalde: de response had geen
        // CORS-header, of er was geen verbinding. Een WAF-blokkade komt hier
        // terecht — de browser ziet de 403 nooit als status.
        console.error('Chat-request bereikte de server niet.', error);
        appendAssistantError(CONNECTION_ERROR_CONTENT);
      },
    });
  }

  return (
    <div>
      {event && <EventHeader event={event} documents={documents} />}

      <Card padding="lg">
        <div className="mb-4 flex items-center gap-2">
          <Bot className="size-5 shrink-0 text-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <CardTitle>AI Assistent</CardTitle>
              <VBadge color="neutral" shape="default">BETA</VBadge>
            </div>
            <Subtitle>
              Stel vragen over het evenement op basis van de gescande documenten
            </Subtitle>
          </div>
        </div>

        <ChatDocumentTags documents={documents} eventId={caseId} />

        <div className="mt-4">
          <ChatMessageList
            entries={entries}
            isPending={sendChat.isPending}
            documents={documents}
            eventId={caseId}
            typingId={typingId}
            onTypingComplete={() => setTypingId(null)}
          />
        </div>

        <div className="mt-4">
          <ChatComposer disabled={sendChat.isPending || typingId !== null} onSend={handleSend} />
        </div>
      </Card>
    </div>
  );
}
